import { useEffect, useState } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { Attesa, Avviso, Intestazione } from '../componenti/Pezzi.tsx'
import { Bottone, Campo, Etichetta, Riquadro, Tabella } from '../componenti/base.tsx'

/**
 * Pagina «Formazione» (decisione di Davide del 31/10/2026, migrazione 490): per chi conduce la
 * formazione del personale, la Direzione e l'amministratore (`formazione.gestisci`).
 *
 * In alto il procedimento, passo per passo; poi lo stato della sede di formazione e dei suoi utenti
 * di prova; in fondo «Ripristina la formazione», con la parola di conferma scritta. Le password nuove
 * arrivano una volta sola, nella risposta del ripristino: restano in questa pagina finche' non la si
 * lascia, si copiano o si stampano, e non si salvano da nessuna parte.
 */

type Incarico = { id: string; ruolo: string; ruolo_nome: string; ambito_tipo: string; ambito_nome: string | null
  valida_a: string | null; scaduto: boolean }
type UtenteDiProva = {
  id: string; email: string; nome: string; cognome: string; stato: string; mfa_attiva: boolean
  /** Da quando il ruolo dell'utenza esige il secondo fattore (493), AAAA-MM-GG; null senza obbligo. */
  mfa_obbligatoria_dal?: string | null
  ultimo_accesso: string | null; sessioni_aperte: number; incarichi: Incarico[]
}
type Stato = {
  sede: { id: string; codice: string; nome: string; attiva: boolean
    progetti: { id: string; codice: string | null; titolo: string; data_fine: string | null }[] }
  utenti: UtenteDiProva[]
  conteggi: { schede: number; percorsi: number; interventi: number; mediazioni: number
    attivita: number; documenti: number; ricontatti: number }
  conferma: string
}
type Credenziale = { email: string; nome: string; cognome: string; ruoli: string[]; password: string }
type Esito = {
  cancellati: Record<string, number>
  creati: { schede: number; percorsi: number; interventi: number; ricontatti: number }
  utenti: number; incarichi: number; sessioni_chiuse: number; file_non_tolti: number
  credenziali: Credenziale[]
}

const messaggio = (e: unknown) =>
  e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.'

/** AAAA-MM-GG fra `giorni` giorni, nel fuso di CIDIS. */
const fraGiorni = (giorni: number) =>
  new Date(Date.now() + giorni * 86_400_000).toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' })
const inItaliano = (giorno: string | null) => (giorno ? giorno.split('-').reverse().join('/') : '—')
const quando = (istante: string | null) => (istante
  ? new Date(istante).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Europe/Rome' })
  : 'mai')

const NOMI_CANCELLATI: Record<string, string> = {
  schede: 'schede', percorsi: 'percorsi', interventi: 'interventi', ricontatti: 'ricontatti',
  mediazioni: 'mediazioni', attivita: 'attività collettive', documenti: 'documenti', servizi: 'servizi erogati',
  erogazioni: 'erogazioni', richieste: 'richieste degli enti', chat_riservate: 'chat riservate',
  agenda: 'impegni dell’agenda',
}

export function Formazione() {
  const [stato, setStato] = useState<Stato | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [fino, setFino] = useState(fraGiorni(7))
  const [parola, setParola] = useState('')
  const [inCorso, setInCorso] = useState(false)
  const [esito, setEsito] = useState<Esito | null>(null)
  const [copiate, setCopiate] = useState(false)

  const carica = () => api.get<Stato>('/formazione').then(setStato).catch((e) => setErrore(messaggio(e)))
  useEffect(() => { void carica() }, [])

  async function ripristina() {
    if (!stato) return
    setErrore(null); setInCorso(true); setCopiate(false)
    try {
      setEsito(await api.post<Esito>('/formazione/ripristina',
        { sede_id: stato.sede.id, conferma: parola, valida_a: fino }))
      setParola('')
      await carica()
    } catch (e) {
      setErrore(messaggio(e))
    } finally {
      setInCorso(false)
    }
  }

  /** Testo da consegnare: una riga per utente, con email e password separate da una tabulazione. */
  const testoCredenziali = (c: Credenziale[]) =>
    c.map((x) => `${x.ruoli.join(', ') || '—'}\t${x.nome} ${x.cognome}\t${x.email}\t${x.password}`).join('\n')

  async function copia() {
    if (!esito) return
    try {
      await navigator.clipboard.writeText(testoCredenziali(esito.credenziali))
      setCopiate(true)
    } catch {
      setErrore('Il browser non ha permesso di copiare: seleziona la tabella e copiala a mano, oppure stampala.')
    }
  }

  /** Stampa il solo riquadro delle credenziali (stile-formazione.css, `.formazione-stampa`). */
  function stampa() {
    document.documentElement.classList.add('formazione-stampa')
    const togli = () => {
      document.documentElement.classList.remove('formazione-stampa')
      window.removeEventListener('afterprint', togli)
    }
    window.addEventListener('afterprint', togli)
    window.print()
  }

  const confermato = !!stato && parola.trim().toLowerCase() === stato.conferma

  return (
    <>
      <Intestazione titolo="Formazione"
                    sottotitolo="La sede di formazione: dati inventati per formare il personale e per la palestra di tutti." />

      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {esito && (
        <section className="formazione-da-stampare">
          <Riquadro titolo="Credenziali degli utenti di prova">
            <Avviso tipo="attenzione">
              Le password si vedono solo adesso: lasciata o ricaricata la pagina non si recuperano più, e per averne
              di nuove serve un altro ripristino. Consegnale a mano, mai per email né in una chat.
            </Avviso>
            <div className="formazione-credenziali">
              <Tabella intestazioni={['Ruolo', 'Utente', 'Email', 'Password']}>
                {esito.credenziali.map((c) => (
                  <tr key={c.email}>
                    <td>{c.ruoli.join(', ') || '—'}</td>
                    <td>{c.nome} {c.cognome}</td>
                    <td>{c.email}</td>
                    <td>{c.password}</td>
                  </tr>
                ))}
              </Tabella>
            </div>
            <p>
              Incarichi validi fino al {inItaliano(fino)}. Gli utenti di prova non devono attivare il secondo fattore.
            </p>
            <div className="formazione-azioni">
              <Bottone variante="primario" onClick={() => void copia()}>
                {copiate ? 'Password copiate' : 'Copia le password'}
              </Bottone>
              <Bottone onClick={stampa}>Stampa</Bottone>
              <Bottone variante="discreto" onClick={() => setEsito(null)}>Ho consegnato le password: nascondile</Bottone>
            </div>
          </Riquadro>
        </section>
      )}

      <Riquadro titolo="Come si conduce una formazione">
        <ol className="formazione-passi">
          <li>
            <strong>Ripristina la formazione</strong>, in fondo a questa pagina. Si cancella quello che hanno fatto
            gli allievi precedenti, tornano le sei schede inventate, gli utenti di prova ricevono password nuove e
            il secondo fattore azzerato, le loro sessioni aperte si chiudono. Scegli fino a quando valgono gli
            incarichi: il giorno della formazione, o pochi giorni dopo.
          </li>
          <li>
            <strong>Consegna le credenziali</strong> agli allievi a mano: su un foglio stampato o dettate, una utenza
            per ruolo. Mai per email e mai in una chat: chi le trova entra. Gli indirizzi finiscono in
            «@formazione.invalid» e nessuna email parte verso nessuno.
          </li>
          <li>
            <strong>Secondo fattore.</strong> Gli utenti di prova non lo devono attivare: vedono solo dati inventati e
            passano da un allievo all’altro. Se qualcuno lo attiva lo stesso, il ripristino lo azzera.
          </li>
          <li>
            <strong>Mostra il lavoro di tutti i giorni</strong> sulle schede inventate (cognome «Formazione»): il
            Cruscotto e il menu; in «Beneficiari» la ricerca, la nuova scheda e l’avviso di doppione; i consensi
            di Moussa, che è minorenne; percorsi, «Registra intervento» e la bozza; documenti, stampa e cronologia;
            l’area riservata con il motivo; lo stesso elenco visto dall’Operatore interno e dall’Operatore esterno.
            Chat riservata e messenger non si aprono sulle schede inventate: si descrivono a voce. La traccia
            completa è nel copione del 30/10/2026 e nella guida rapida (docs/formazione).
          </li>
          <li>
            <strong>Fai provare la palestra.</strong> Ognuno entra con la propria utenza e preme «Palestra» in alto,
            accanto al nome: con i suoi stessi ruoli vede solo la sede di formazione, la fascia gialla lo ricorda
            su ogni pagina, posta e messenger restano chiusi. Esce con «Esci dalla palestra», o uscendo dal
            gestionale.
          </li>
          <li>
            <strong>Alla fine ripristina di nuovo.</strong> Le password consegnate smettono di valere, le sessioni
            degli utenti di prova si chiudono, le schede tornano sei. Il registro degli eventi conserva tutto: in
            «Registro eventi» il filtro «Palestra» separa le prove dal lavoro vero.
          </li>
        </ol>
      </Riquadro>

      {!stato && !errore && <Attesa />}
      {stato && (
        <>
          <Riquadro titolo={`${stato.sede.nome} (${stato.sede.codice})`}
                    azioni={stato.sede.attiva ? <Etichetta tono="positivo">attiva</Etichetta>
                      : <Etichetta tono="critico">non attiva: la palestra non funziona</Etichetta>}>
            <p>
              {stato.sede.progetti.length
                ? <>Progetto: {stato.sede.progetti.map((p) => `${p.codice ? `${p.codice} · ` : ''}${p.titolo}`).join('; ')}.</>
                : 'Nessun progetto di formazione collegato: il ripristino non può ricreare i percorsi.'}
            </p>
            <dl className="formazione-numeri">
              {([['schede', 'Schede'], ['percorsi', 'Percorsi'], ['interventi', 'Interventi'],
                ['mediazioni', 'Mediazioni'], ['ricontatti', 'Ricontatti'], ['attivita', 'Attività collettive'],
                ['documenti', 'Documenti']] as const).map(([chiave, nome]) => (
                <div key={chiave}><dt>{nome}</dt><dd>{stato.conteggi[chiave]}</dd></div>
              ))}
            </dl>
          </Riquadro>

          <Riquadro titolo="Utenti di prova" denso>
            <Tabella intestazioni={['Utente', 'Ruolo', 'Incarico fino al', 'Ultimo accesso', 'Secondo fattore', 'Sessioni aperte']}
                     vuoto="Nessun utente di prova: si creano con lo strumento della formazione.">
              {stato.utenti.map((u) => (
                <tr key={u.id}>
                  <td>{u.nome} {u.cognome}<br /><span className="tenue">{u.email}</span>
                    {u.stato !== 'attivo' && <> <Etichetta tono="critico">{u.stato}</Etichetta></>}</td>
                  <td>{u.incarichi.map((i) => `${i.ruolo_nome}${i.ambito_nome ? ` · ${i.ambito_nome}` : ''}`).join('; ') || '—'}</td>
                  <td>{u.incarichi.map((i) => (
                    <span key={i.id}>{inItaliano(i.valida_a)}{i.scaduto && <> <Etichetta tono="attenzione">scaduto</Etichetta></>} </span>
                  ))}</td>
                  <td>{quando(u.ultimo_accesso)}</td>
                  <td>{u.mfa_attiva ? 'attivo'
                    : !u.mfa_obbligatoria_dal ? 'non obbligatorio'
                      : u.mfa_obbligatoria_dal <= fraGiorni(0) ? 'da attivare al primo accesso'
                        : `obbligatorio dal ${inItaliano(u.mfa_obbligatoria_dal)}`}</td>
                  <td>{u.sessioni_aperte}</td>
                </tr>
              ))}
            </Tabella>
          </Riquadro>

          <Riquadro titolo="Ripristina la formazione">
            <p>
              Cancella tutto quello che è nato nella sede e nel progetto di formazione — schede, percorsi,
              interventi, mediazioni, ricontatti, documenti, attività collettive — e rimette le sei schede
              inventate. Agli utenti di prova dà password nuove, che vedrai una volta sola, azzera il secondo
              fattore e chiude le sessioni. Le caselle di gruppo finte restano spente. Non tocca nessun dato delle
              altre sedi; il registro degli eventi non si cancella.
            </p>
            {esito && (
              <Avviso tipo="successo">
                Ripristinata: cancellati {Object.entries(esito.cancellati).filter(([, n]) => n > 0)
                  .map(([k, n]) => `${n} ${NOMI_CANCELLATI[k] ?? k}`).join(', ') || 'nessun dato'};
                ricreate {esito.creati.schede} schede con {esito.creati.interventi} interventi;
                {' '}{esito.utenti} utenti di prova con password nuove, {esito.sessioni_chiuse} sessioni chiuse.
                {esito.file_non_tolti > 0 && ` ${esito.file_non_tolti} file di documenti non sono stati tolti dall’archivio: avvisa l’assistenza.`}
              </Avviso>
            )}
            <div className="formazione-conferma">
              <Campo tipo="date" etichetta="Gli incarichi degli utenti di prova valgono fino al"
                     valore={fino} cambia={setFino} min={fraGiorni(0)} max={fraGiorni(90)}
                     aiuto="Al massimo novanta giorni da oggi." />
              <Campo etichetta={`Per confermare scrivi «${stato.conferma}»`} valore={parola} cambia={setParola}
                     autoComplete="off" />
              <div>
                <Bottone variante="critico" disabled={!confermato || !fino} inCorso={inCorso}
                         onClick={() => void ripristina()}>
                  Ripristina la formazione
                </Bottone>
              </div>
            </div>
          </Riquadro>
        </>
      )}
    </>
  )
}
